import { useMemo } from "react";

import { getRoute, type AppRouteDef } from "@/app/routes";
import { NAVIGATION, type NavItemDef } from "@/components/layout/navigation";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import type { PermissionOrWildcard } from "@/features/rbac/permissions/catalog";

/** Entrada de menú ya resuelta: visible, con su destino real y sus hijos. */
export interface ResolvedNavItem {
  readonly routeId: string;
  readonly name: string;
  readonly href: string;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly children: readonly ResolvedNavItem[];
  readonly mobileOverflow: boolean;
}

type Checker = {
  can: (permission: PermissionOrWildcard) => boolean;
  canAll: (permissions: readonly PermissionOrWildcard[]) => boolean;
  canAny: (permissions: readonly PermissionOrWildcard[]) => boolean;
};

/** ¿La ruta es alcanzable con los permisos actuales? */
function isRouteGranted(route: AppRouteDef | undefined, checker: Checker): boolean {
  if (!route) return false;
  if (route.access === "public") return true;
  if (!route.permission) return true;

  if (Array.isArray(route.permission)) {
    const list = route.permission as readonly PermissionOrWildcard[];
    return route.mode === "all" ? checker.canAll(list) : checker.canAny(list);
  }

  return checker.can(route.permission as PermissionOrWildcard);
}

function resolveItem(item: NavItemDef, checker: Checker): ResolvedNavItem | null {
  const route = getRoute(item.routeId);
  const ownAccess = isRouteGranted(route, checker);

  const children = (item.children ?? [])
    .map((child) => resolveItem(child, checker))
    .filter((child): child is ResolvedNavItem => child !== null);

  // Un padre sigue visible si alguno de sus hijos lo está, aunque su propia
  // ruta esté vedada: es el caso de Reportes para quien solo tiene el
  // inventario de software.
  if (!ownAccess && children.length === 0) return null;

  // Si la ruta propia no es alcanzable, el padre enlaza al primer hijo visible.
  // Enlazar a la ruta propia mandaría a un redirect denegado.
  const href = ownAccess ? (item.hrefOverride ?? route?.path ?? "/") : children[0].href;

  return {
    routeId: item.routeId,
    name: item.name,
    href,
    icon: item.icon,
    children,
    mobileOverflow: item.mobileOverflow ?? false,
  };
}

/**
 * Entradas de menú visibles para la persona autenticada.
 *
 * Es el ÚNICO filtro de navegación: lo consumen por igual la barra lateral de
 * escritorio, el panel lateral de tableta y la barra inferior de móvil. Antes
 * había tres filtros distintos, escritos a mano y con reglas que no coincidían
 * entre sí.
 */
export function useVisibleNavigation(): readonly ResolvedNavItem[] {
  const { can, canAll, canAny, isLoading } = usePermissions();

  return useMemo(() => {
    if (isLoading) return [];

    const checker: Checker = { can, canAll, canAny };

    return NAVIGATION.map((item) => resolveItem(item, checker)).filter(
      (item): item is ResolvedNavItem => item !== null
    );
  }, [can, canAll, canAny, isLoading]);
}
